import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { ApplicationUser } from 'src/app/models/application-user/application-user';
import { PosDocumentStatus, PosTransactionStatus, PosTransactionType } from 'src/app/models/enums/pos-enums';
import { AlertService } from './alert.service';
import { SignalRService } from './signalr.service';

/** Sunucudan gelen değişen POS işlemi (PosTransactionNotification). Kart bilgisi taşımaz. */
export interface PosTransactionChanged {
  id: number;
  reference: string;
  type: PosTransactionType;
  status: PosTransactionStatus;
  /** Yeni kayıtta null. */
  previousStatus?: PosTransactionStatus | null;
  documentStatus: PosDocumentStatus;
  /** TL — cihaz belgesinin tutarı. */
  amount: number;
  posDeviceId: number;
  deviceName?: string | null;
  originalTransactionId?: number | null;
  errorMessage?: string | null;
  completedAt?: string | null;
}

/** POS İşlemleri ekranının menü bağlantısı (SYS_090 / MenuItems.Link). */
const POS_TRANSACTIONS_MENU_LINK = 'case/pos-transactions';

const FINAL_STATUSES = [PosTransactionStatus.Approved, PosTransactionStatus.Declined, PosTransactionStatus.Cancelled];

/**
 * POS cihaz işlemlerinin canlı akışı (backend `PosHub`, pos-entegrasyonu.md K23).
 *
 * İşlemin durumu sunucuda değişince (Pavo geri çağrısı, mutabakat, bekleme penceresi) işlem
 * tenant'ın açık ekranlarına itilir. Bu servis:
 *  - sonuçlanan işlemi (onay, red, iptal) her ekranda bildirim olarak gösterir — tıklanınca
 *    POS İşlemleri açılır;
 *  - `changed$` ile POS İşlemleri ekranının listesini ve KPI'larını tazeler.
 *
 * Yalnızca menüsünde POS İşlemleri ekranı olan kullanıcı bağlanır (yetkili personel); sunucuda
 * hub adresinin yetki kaydı (SYS_096) aynı rolü taşır. Bağlantı oturum açılınca kurulur
 * (ApplicationUserService), çıkışta kapatılır.
 *
 * Gönderim penceresi (PosSendModalComponent) açık olan işlem için bildirim gösterilmez: kasiyer
 * sonucu pencerede görüyor. Pencere "Arka planda bırak" ile kapanınca bildirim yine gelir.
 */
@Injectable({ providedIn: 'root' })
export class PosHubService {
  private readonly hubUrl = '/posHub';
  private readonly changed = new Subject<PosTransactionChanged>();
  /** Gönderim penceresinde izlenen işlemler — bildirimleri pencere gösterir. */
  private readonly watched = new Set<number>();

  /** Durumu değişen ya da yeni oluşan işlemler. */
  readonly changed$: Observable<PosTransactionChanged> = this.changed.asObservable();

  /** Dinleyicinin tek sahibi burası; iki kez bağlanırsa her sonuç iki bildirim üretirdi. */
  private registered = false;

  constructor(
    private signalR: SignalRService,
    private alertService: AlertService,
    private router: Router,
  ) {}

  connect(user: ApplicationUser | null | undefined): void {
    if (this.registered || !PosHubService.canSeePosTransactions(user)) {
      return;
    }

    const connection = this.signalR.start(this.hubUrl);
    if (!connection) {
      // Token yoksa start() null döner; oturum açıldığında tekrar denenir.
      return;
    }

    this.registered = true;
    connection.on('receivePosTransactionChanged', (transaction: PosTransactionChanged) => {
      if (!transaction?.id) {
        return;
      }
      this.changed.next(transaction);
      this.notify(transaction);
    });
  }

  /** Çıkışta çağrılır; bayrak sıfırlanır ki sonraki oturumda yeniden bağlanılsın. */
  disconnect(): void {
    this.registered = false;
    this.watched.clear();
    this.signalR.stop(this.hubUrl);
  }

  /** Gönderim penceresi işlemi izlemeye başladı; bu işlem için bildirim gösterilmez. */
  watch(id: number): void {
    this.watched.add(id);
  }

  unwatch(id: number): void {
    this.watched.delete(id);
  }

  private notify(transaction: PosTransactionChanged): void {
    // Yalnızca sonuçlanma anı: aynı durumun tekrarı ya da ara durum (cihazda, askıda) bildirilmez.
    // Yeni kayıt (previousStatus boş) da bildirilmez: gönderimde hemen reddedilen istek cihaza
    // ulaşmadı, hatayı gönderen kasiyer penceresinde görüyor.
    if (this.watched.has(transaction.id)
        || transaction.previousStatus == null
        || !FINAL_STATUSES.includes(transaction.status)
        || transaction.previousStatus === transaction.status) {
      return;
    }

    const isVoid = transaction.type === PosTransactionType.Void;
    const isRefund = transaction.type === PosTransactionType.Refund;
    const amount = `${formatTl(transaction.amount)} ₺`;
    const device = escapeHtml(transaction.deviceName || 'POS cihazı');
    const open = () => this.router.navigate(['/case/pos-transactions']);
    const body = (extra = '') =>
      `<div class='fw-semibold'>${amount}</div><div class='small'>${device} · ${escapeHtml(transaction.reference)}</div>${extra}`;

    if (transaction.status === PosTransactionStatus.Approved) {
      const documentFailed = transaction.documentStatus === PosDocumentStatus.Failed
        ? `<div class='small text-warning mt-1'>Belge oluşmadı; cihazdan tamamlayın.</div>` : '';
      this.alertService.showClickableToastHtml(
        documentFailed ? 'warning' : 'success',
        isVoid ? 'POS iptali tamamlandı — karta iade edildi'
          : isRefund ? 'POS satır iadesi tamamlandı' : 'POS ödemesi onaylandı',
        body(documentFailed), open, documentFailed ? 12000 : 6000);
      return;
    }

    if (transaction.status === PosTransactionStatus.Declined) {
      const reason = transaction.errorMessage
        ? `<div class='small mt-1'>${escapeHtml(transaction.errorMessage)}</div>` : '';
      this.alertService.showClickableToastHtml(
        'error', isVoid ? 'POS iptali yapılamadı' : isRefund ? 'POS iadesi yapılamadı' : 'POS ödemesi reddedildi',
        body(reason), open, 10000);
      return;
    }

    this.alertService.showClickableToastHtml(
      'info', isVoid ? 'POS iptal isteği geri çekildi' : isRefund ? 'POS iade isteği geri çekildi' : 'POS isteği iptal edildi',
      body(), open, 6000);
  }

  private static canSeePosTransactions(user: ApplicationUser | null | undefined): boolean {
    return (user?.menuItemList || []).some((menu) =>
      (menu.childrenMenuItem || []).some((child) =>
        (child.link || '').replace(/^\/+/, '').startsWith(POS_TRANSACTIONS_MENU_LINK)));
  }
}

function formatTl(value: number): string {
  return (value || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]!));
}
